import React, { useState, useEffect } from 'react';
import RichTextContent from '../components/RichTextContent';
import { Link } from 'react-router-dom';
import { ChevronRight, Calendar, Tag, Loader2, ExternalLink } from 'lucide-react';
import { meshNewsApi, brandsApi } from '../services/api';

const NewsPage = () => {
  const [news, setNews] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [newsRes, brandsRes] = await Promise.all([
          meshNewsApi.getAll(),
          brandsApi.getAll()
        ]);
        setNews(newsRes.data);
        setBrands(brandsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getBrandName = (brandId) => {
    const brand = brands.find(b => b.id === brandId);
    return brand ? brand.name : null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="bg-white" data-testid="news-page">
      {/* Hero Section */}
      <section className="bg-gray-900 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-400 mb-8">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-white">News</span>
          </nav>

          <h1 className="text-4xl md:text-5xl font-light text-white mb-4">
            Mesh Lighting News
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl">
            Stay updated with the latest developments, product launches, and project showcases from Mesh Lighting and our partner brands.
          </p>
        </div>
      </section>

      {/* Featured Article */}
      {news.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <Link to={`/news/${news[0].id}`} className="aspect-[16/10] overflow-hidden rounded-lg group">
                <img
                  src={news[0].image}
                  alt={news[0].title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </Link>
              <div>
                <div className="flex items-center flex-wrap gap-2 mb-4">
                  <span className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full">
                    <Tag className="h-3 w-3 mr-1" />
                    {news[0].category}
                  </span>
                  {news[0].brandTags?.map(brandId => {
                    const brandName = getBrandName(brandId);
                    return brandName ? (
                      <Link
                        key={brandId}
                        to={`/brand/${brandId}`}
                        className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full hover:bg-blue-200 transition-colors"
                      >
                        {brandName}
                      </Link>
                    ) : null;
                  })}
                  <span className="flex items-center text-sm text-gray-500">
                    <Calendar className="h-4 w-4 mr-1" />
                    {news[0].date}
                  </span>
                </div>
                <Link to={`/news/${news[0].id}`}>
                  <h2 className="text-3xl font-medium text-gray-900 mb-4 hover:text-gray-700 transition-colors">
                    {news[0].title}
                  </h2>
                </Link>
                <RichTextContent preview content={news[0].excerpt} className="text-gray-600 leading-relaxed mb-6 line-clamp-4 [&_*]:my-0 [&_p]:text-inherit [&_*]:text-inherit" testId="news-featured-excerpt" />
                <Link
                  to={`/news/${news[0].id}`}
                  className="inline-flex items-center px-6 py-3 bg-gray-900 text-white font-medium rounded-sm hover:bg-gray-800 transition-colors"
                  data-testid="featured-news-link"
                >
                  Read Full Article
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* All News */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-medium text-gray-900 mb-8">All News</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {news.map((item) => (
              <Link
                key={item.id}
                to={`/news/${item.id}`}
                className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                data-testid={`news-card-${item.id}`}
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center flex-wrap gap-2 mb-3">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {item.category}
                    </span>
                    {item.brandTags?.map(brandId => {
                      const brandName = getBrandName(brandId);
                      return brandName ? (
                        <span
                          key={brandId}
                          className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full"
                        >
                          {brandName}
                        </span>
                      ) : null;
                    })}
                    <span className="text-gray-300">•</span>
                    <span className="text-xs text-gray-500">{item.date}</span>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2 group-hover:text-gray-700 transition-colors">
                    {item.title}
                  </h3>
                  <RichTextContent preview content={item.excerpt} className="text-gray-600 text-sm line-clamp-2 mb-4 [&_*]:my-0 [&_p]:text-inherit [&_*]:text-inherit" testId={`news-excerpt-${item.id}`} />
                  <span className="inline-flex items-center text-sm text-gray-900 font-medium group-hover:underline">
                    Read Article
                    <ChevronRight className="ml-1 h-3 w-3" />
                  </span>
                </div>
              </Link>
            ))}
          </div>

          {!news.length && (
            <p className="text-gray-500 text-center py-12">No news articles available</p>
          )}
        </div>
      </section>
    </div>
  );
};

export default NewsPage;
